import React from 'react';
import { BeadSequence } from '../../constants/rosarySequences';
import { hapticFeedback, useHapticPreference } from '../../utils/hapticFeedback';

interface BeadProps {
  bead: BeadSequence;
  index: number;
  totalBeads: number;
  isActive: boolean;
  isPast: boolean;
  onClick: () => void;
}

export const Bead: React.FC<BeadProps> = ({
  bead,
  index,
  totalBeads,
  isActive,
  isPast,
  onClick
}) => {
  const hapticEnabled = useHapticPreference();
  
  const handleClick = () => {
    // Trigger haptic feedback based on bead type and state
    if (hapticEnabled) {
      if (bead.type === 'cross') {
        hapticFeedback.transition();
      } else if (bead.type === 'medal') {
        hapticFeedback.stepComplete();
      } else if (bead.type === 'large-bead') {
        hapticFeedback.beadSelect();
      } else {
        hapticFeedback.beadTap();
      }
    }
    
    onClick();
  };
  const getBeadStyles = () => {
    const baseClasses = 'cursor-pointer transition-all duration-500 transform hover:scale-102 flex-shrink-0 relative';

    // Enhanced visual states - remove ring classes that conflict with realistic styling
    let stateClasses = '';
    if (isActive) {
      stateClasses = 'scale-110';
    } else if (isPast) {
      stateClasses = 'opacity-80 scale-95';
    } else {
      stateClasses = 'opacity-85 hover:opacity-100';
    }

    // Realistic 3D bead styles with CSS
    // large-bead = Our Father beads (larger, more prominent)
    // small-bead = Hail Mary beads (smaller)
    switch (bead.type) {
      case 'cross':
        return `${baseClasses} ${stateClasses} w-12 h-14 flex flex-col items-center justify-center realistic-cross ${isPast ? 'cross-completed' : isActive ? 'cross-active' : 'cross-default'}`;

      case 'large-bead':
        return `${baseClasses} ${stateClasses} w-11 h-11 realistic-bead large-bead ${isPast ? 'bead-completed' : isActive ? 'bead-active' : 'bead-default'}`;

      case 'small-bead':
        return `${baseClasses} ${stateClasses} w-8 h-8 realistic-bead small-bead ${isPast ? 'bead-completed' : isActive ? 'bead-active' : 'bead-default'}`;

      case 'medal':
        return `${baseClasses} ${stateClasses} w-10 h-10 realistic-medal ${isPast ? 'medal-completed' : isActive ? 'medal-active' : 'medal-default'} flex items-center justify-center`;

      default:
        return `${baseClasses} ${stateClasses} w-6 h-6 realistic-bead small-bead bead-default`;
    }
  };

  const renderBeadContent = () => {
    switch (bead.type) {
      case 'cross':
        return (
          <div className="text-white relative flex items-center justify-center w-10 h-12">
            {/* Vertical bar (full height) */}
            <div className="absolute w-2 h-10 bg-white rounded-sm"></div>
            {/* Horizontal bar (positioned in upper third) */}
            <div className="absolute w-7 h-2 bg-white rounded-sm" style={{ top: '25%' }}></div>
          </div>
        );

      case 'medal':
        // For realistic medals, don't render inner content - styling is handled by CSS
        return null;

      default:
        return null;
    }
  };


  return (
    <div className="relative bead-container">
      {/* Chain connection - appears above each bead except the last (visually top) bead */}
      {index < totalBeads - 1 && (
        <div className="chain-link absolute -top-5 left-1/2 transform -translate-x-1/2 w-2 h-6"></div>
      )}

      <button
        onClick={handleClick}
        className={getBeadStyles()}
        title={bead.label || `${bead.type} - ${bead.prayers.join(', ')}`}
        aria-label={bead.label || `${bead.type} bead for ${bead.prayers.join(' and ')}`}
      >
        {renderBeadContent()}

        {/* Completion checkmark for completed beads */}
        {isPast && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg">
            ✓
          </div>
        )}

        {/* Active bead glow effect */}
        {isActive && (
          <>
            {/* Behind glow - matches bead shape */}
            <div className={`absolute inset-0 bg-gradient-radial from-yellow-400 to-transparent opacity-30 blur-sm scale-150 -z-10 ${bead.type === 'cross' ? 'rounded-sm' : 'rounded-full'}`} />
            {/* Overlay shimmer - matches bead shape */}
            <div className={`absolute inset-0 bg-gradient-to-br from-yellow-200 via-transparent to-orange-200 opacity-25 animate-pulse ${bead.type === 'cross' ? 'rounded-sm' : 'rounded-full'}`} />
          </>
        )}
      </button>
    </div>
  );
};